import React, { useState, useEffect } from 'react';
import { Save, FileText } from 'lucide-react';
import { toast } from 'sonner';

const AdminHistoirePage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({
    intro_text: '',
    quote_text: '',
    stat_year: '2009',
    stat_events: '1200',
    stat_attendees: '240K',
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Fetch content
  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await fetch(`${backendUrl}/api/admin/histoire`, {
        headers: {
          'Authorization': `Basic ${auth}`,
        },
      });
      const data = await response.json();
      if (data.content) {
        setContent(data.content);
      }
    } catch (error) {
      console.error('Error fetching histoire content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const auth = localStorage.getItem('admin_auth');
    setSaving(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/histoire`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${auth}`,
        },
        body: JSON.stringify(content),
      });

      if (response.ok) {
        toast.success('Contenu Histoire mis à jour !');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error saving histoire:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C9A84C]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-histoire-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Page Histoire</h1>
          <p className="text-gray-400 mt-1">
            Gérez le contenu de la page Histoire/Timeline
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-[#C9A84C] text-black rounded-lg hover:bg-[#B39840] transition-colors disabled:opacity-50"
          data-testid="save-histoire-btn"
        >
          <Save className="w-5 h-5" />
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
      </div>

      {/* Intro Text */}
      <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#C9A84C]" />
          Texte d'introduction
        </h3>
        <textarea
          value={content.intro_text}
          onChange={(e) => setContent({ ...content, intro_text: e.target.value })}
          className="w-full h-32 px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#C9A84C] resize-none"
          placeholder="Née d'une passion pour la musique latine..."
          data-testid="intro-text-input"
        />
        <p className="text-xs text-gray-500 mt-2">
          Ce texte apparaît en haut de la page Histoire
        </p>
      </div>

      {/* Quote */}
      <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Citation</h3>
        <textarea
          value={content.quote_text}
          onChange={(e) => setContent({ ...content, quote_text: e.target.value })}
          className="w-full h-24 px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#C9A84C] resize-none italic"
          placeholder="More than a party, it really is a one night ticket..."
          data-testid="quote-text-input"
        />
        <p className="text-xs text-gray-500 mt-2">
          Citation inspirante affichée sous le texte d'introduction
        </p>
      </div>

      {/* Stats */}
      <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Statistiques clés</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Année de création</label>
            <input
              type="text"
              value={content.stat_year}
              onChange={(e) => setContent({ ...content, stat_year: e.target.value })}
              className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#C9A84C]"
              placeholder="2009"
              data-testid="stat-year-input"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Nombre d'événements</label>
            <input
              type="text"
              value={content.stat_events}
              onChange={(e) => setContent({ ...content, stat_events: e.target.value })}
              className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#C9A84C]"
              placeholder="1200+"
              data-testid="stat-events-input"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Participants</label>
            <input
              type="text"
              value={content.stat_attendees}
              onChange={(e) => setContent({ ...content, stat_attendees: e.target.value })}
              className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#C9A84C]"
              placeholder="240K+"
              data-testid="stat-attendees-input"
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-4">
          Ces statistiques apparaissent en haut de la page Histoire
        </p>
      </div>

      {/* Timeline Note */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <p className="text-sm text-blue-300">
          💡 <strong>Note :</strong> Pour gérer la timeline (2009, 2011, 2015, etc.), utilisez l'onglet <strong>"Timeline"</strong> dans le menu de gauche.
        </p>
      </div>

      {/* Preview */}
      <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Aperçu</h3>
        <div className="bg-black/40 rounded-lg p-6 space-y-4">
          <p className="text-white leading-relaxed">
            {content.intro_text || "Texte d'introduction..."}
          </p>
          <p className="text-gray-400 italic text-center">
            "{content.quote_text || "Citation..."}"
          </p>
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-[#C9A84C]">{content.stat_year}</div>
              <div className="text-xs text-gray-400 mt-1">DEPUIS</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#C9A84C]">{content.stat_events}</div>
              <div className="text-xs text-gray-400 mt-1">ÉVÉNEMENTS</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#C9A84C]">{content.stat_attendees}</div>
              <div className="text-xs text-gray-400 mt-1">PARTICIPANTS</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHistoirePage;
